import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HeroSection = () => {
  return (
    <section className="relative min-h-[85vh] md:min-h-[90vh] flex items-center overflow-hidden">
      {/* Background Layers */}
      <div className="absolute inset-0 bg-gradient-fabric" />
      <div className="absolute inset-0 bg-fabric-texture" />
      <div className="absolute inset-0 bg-floral-pattern opacity-30" />
      
      {/* Silver Floral Overlay SVG */}
      <svg
        className="absolute top-0 right-0 w-1/2 h-full opacity-5 pointer-events-none"
        viewBox="0 0 400 800"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M200 0C200 0 350 150 350 300C350 450 200 500 200 650C200 800 350 800 350 800"
          stroke="hsl(210 8% 75%)"
          strokeWidth="1"
          fill="none"
        />
        <circle cx="200" cy="300" r="80" stroke="hsl(210 8% 75%)" strokeWidth="0.5" fill="none" />
        <circle cx="200" cy="300" r="120" stroke="hsl(210 8% 75%)" strokeWidth="0.3" fill="none" />
      </svg>

      {/* Main Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center md:text-left md:mx-0">
          {/* Decorative Line */}
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: '60px' }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-px bg-gradient-silver mx-auto md:mx-0 mb-6"
          />

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-silver/20 bg-card/50 backdrop-blur-sm mb-6"
          >
            <Sparkles className="h-4 w-4 text-silver" />
            <span className="text-sm text-silver font-sans">Handcrafted with Barakah</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground leading-tight mb-6"
          >
            Turning <span className="text-gradient-silver">Nikah</span> Moments
            <br />
            into <span className="text-ivory">Timeless Memories</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="font-body text-lg md:text-xl text-muted-foreground max-w-xl mx-auto md:mx-0 mb-8 leading-relaxed"
          >
            Handcrafted bridal wear and gifts with personalised duas, 
            Quranic calligraphy, and pearl embellishments. 
            <span className="text-silver italic"> Made with love and barakah.</span>
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
          >
            <Link to="/products">
              <Button variant="ivory" size="lg" className="w-full sm:w-auto group">
                Shop Now
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link to="/customize">
              <Button variant="silver" size="lg" className="w-full sm:w-auto">
                <Sparkles className="h-4 w-4" />
                Customise Your Order
              </Button>
            </Link>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="flex flex-wrap items-center justify-center md:justify-start gap-6 mt-12 pt-8 border-t border-border/30"
          >
            <div className="flex items-center gap-2">
              <span className="text-2xl font-display text-silver-light">500+</span>
              <span className="text-sm text-muted-foreground">Happy Brides</span>
            </div>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <span className="text-2xl font-display text-silver-light">4.9★</span>
              <span className="text-sm text-muted-foreground">Rating</span>
            </div>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <span className="text-2xl font-display text-silver-light">100%</span>
              <span className="text-sm text-muted-foreground">Handmade</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative Pearl Elements */}
      <div className="absolute bottom-20 right-10 hidden lg:block">
        <motion.div
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          className="h-3 w-3 rounded-full bg-pearl shadow-silver"
        />
      </div>
      <div className="absolute top-40 right-1/4 hidden lg:block">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          className="h-2 w-2 rounded-full bg-pearl-pink shadow-silver opacity-60"
        />
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default HeroSection;
